import PropTypes from 'prop-types';
import React, { useState } from 'react';

import { Meta } from '$partials/global';
import {
    ModuleDispatcher,
    ModulePrimaryContentCarouselBlock
} from '$partials/modules';
import { Header, Footer, StickyCTAButtons } from '$partials/global/index.js';
import Image from '$partials/media/image';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @param {PropTypes.InferProps<typeof propTypes>} props
 */
const PersonDetailPage = (props) => {
    const page = props.data.page;
    const newsletterModule = page?.modules?.find((item) => item.__typename === 'NewsletterBlock');
    const firstAccordion = page?.modules?.find((item) => item.__typename === 'AccordionBlock');

    // state
    const [selectedAccordionId, setSelectedAccordionId] = useState(firstAccordion?.id);

    const listingPageLink = {
        'anchor': null,
        'customUrl': './',
        'email': '',
        'file': null,
        'linkTo': 'custom_url',
        'page': null,
        'phone': ''
    };

    return (
        <>
            <Meta
                description={page.searchDescription || page.listingText}
                domain={props.domain}
                image={page.listingImage}
                page={page} />

            <Header
                {...props?.data?.navigation}
                {...props?.data?.ticker}
                pageType={page?.__typename}
                standfirst={page?.standfirst} />

            <StickyCTAButtons
                cta1={page?.stickyCta1?.[0]}
                cta2={page?.stickyCta2?.[0]} />

            <main id='main-content'>
                <section className='layout-container'>
                    <div className='layout-grid-negative-gutter'>
                        <div className='sm:w-6/12 md:w-12/12 md:flex'>
                            <div className='layout-grid-col-padding md:w-1/12' />

                            <div className='layout-grid-col-padding pb-60 md:pb-0 md:w-4/12'>
                                {page?.personType && (
                                    <p className='type-style-4 pb-20 sm:pb-30'>
                                        {page.personType.title}
                                    </p>
                                )}

                                {page?.title && (
                                    <h1 className='type-style-2 pb-20 sm:pb-30'>
                                        {page.title}
                                    </h1>
                                )}

                                <p className='type-style-4 pb-15'>
                                    {page?.role && page?.department && (
                                        <span>{page.role}, </span>
                                    )}

                                    {page?.role && !page?.department && (
                                        <span>{page.role}</span>
                                    )}

                                    {page?.department && (
                                        <span>{page.department}</span>
                                    )}
                                </p>

                                {page?.associatesTitle && (
                                    <p className='type-style-4'>
                                        {page.associatesTitle}
                                    </p>
                                )}
                            </div>

                            <div className='layout-grid-col-padding -mb-30 sm:mb-0 md:pb-30 md:w-4/12 pointer-events-none'>
                                <Image
                                    {...page?.image}
                                    alt={`Image of ${page.title}`} />
                            </div>
                        </div>
                    </div>
                </section>

                <div className='bg-white relative'>
                    {page?.modules?.map((module, idx) => {
                        if (module.__typename === 'AccordionBlock') {
                            module.isSelectedAccordion = (module.id === selectedAccordionId);
                            module.setIsSelectedAccordion = setSelectedAccordionId;
                        }

                        return (
                            <ModuleDispatcher
                                index={idx}
                                isAccordionOrSection={true}
                                key={idx}
                                {...module} />
                        );
                    })}
                </div>
                {page?.relatedPeople?.edges?.length > 0 && (
                    <div>
                        <div className='border-t border-t-charcoal' />
                        <ModulePrimaryContentCarouselBlock
                            heading='Explore The Community'
                            pages={page?.relatedPeople?.edges.map(itm => {
                                itm.node.__typename = 'RelatedPeople';
                                return {
                                    page: itm.node
                                };
                            })}
                            viewAllLink={listingPageLink}/>
                    </div>
                )}
            </main>

            <Footer
                hasNewsletterModule={newsletterModule}
                {...props?.data?.footer}
                {...props?.data?.newsletter} />
        </>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

const propTypes = {
    data: PropTypes.shape({
        page: PropTypes.object,
        footer: PropTypes.object,
        navigation: PropTypes.object,
        newsletter: PropTypes.object,
        ticker: PropTypes.object,
    }),
    domain: PropTypes.string,
};

PersonDetailPage.propTypes = propTypes;

export default PersonDetailPage;
